import Icon from '@/components/UI/Icon';
import Indicator from '@/components/UI/Indicator';
import { _get, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { AltId, Animal, AnimalIdentifier } from '@/model/Animal.model';
import type { ID } from '@/model/Common.model';
import React, { CSSProperties, FC, MouseEventHandler, RefCallback, useCallback, useMemo, useState } from 'react';
import { List } from 'react-window';
import { ShowState, WorkflowSetup } from './Workflow.model';
import Tooltip from '@/components/UI/Tooltip';
import { modalAction } from '@/utils/modal';
import { useDispatch } from 'react-redux';
import { animalIdsRecord } from '@/constants/utils';
import { EditSubjectIdProps } from '@/components/Modals/EditSubjectId/EditSubjectId';
import {
  useAlerts,
  useChangeAnimalActions,
  useSelectedAnimal,
  useSelectedCage,
  useSubjects,
  useUpdateSubjectActions,
} from '@/utils/workflow/useWorkflow.tsx';
import { ellipsisFromStart } from '@/helpers';

export interface AnimalSelectPanelProps {
  state: ShowState;
  workflowSetup: WorkflowSetup;
  disabled: boolean;
}

const inActiveMenuItemClasses = 'br2 pointer f6 hover-blue hover-bg-moon-gray db w-100 near-black pv2 ph3';
const activeMenuItemClasses = 'br2 pointer f6 bg-near-white blue db w-100 near-black pv2 ph3';
export const ELLIPSIS_LENGTH = 6;

const AnimalSelectPanel: FC<AnimalSelectPanelProps> = ({ state, workflowSetup, disabled }) => {
  const { checklist, collections } = state;
  const { openModal, closeModal } = modalAction(useDispatch());
  const { selectedAnimal } = useSelectedAnimal();
  const { selectedCage } = useSelectedCage();
  const { changeAnimal, changeCage } = useChangeAnimalActions();
  const { idToSearch, idToDisplay } = workflowSetup;
  const { availableSubjects, originalSubjects, filteredCageIds } = useSubjects();
  const { updateSingleSubject } = useUpdateSubjectActions();
  const { animalAlerts } = useAlerts();

  const deceased = availableSubjects?.filter(
    (subject) => _notNil(subject.terminated_at) && _isNil(subject.deceasedCageId)
  );
  const cageIds = useMemo(() => filteredCageIds ?? collections.map((cage) => cage.id), [filteredCageIds, collections]);
  const [height, setHeight] = useState<number | null>(null);
  const stageRef = useCallback<RefCallback<HTMLDivElement>>((instance) => {
    if (_notNil(instance)) {
      setHeight(instance.getBoundingClientRect().height);
    }
  }, []);

  const filterSubjects = (subjectArray: Array<Animal>) =>
    subjectArray.filter((subject) => subject.cage_id === selectedCage || subject.deceasedCageId === selectedCage);

  const selectedSubjects = filterSubjects(availableSubjects ?? []);
  const originalSelectedSubjects = selectedCage === 'deceased' ? deceased : filterSubjects(originalSubjects ?? []);

  const [subjectsByCage, cageHasAlerts] = useMemo(() => {
    const subjectsMap = new Map<ID, Array<Animal>>();
    const alertsMap = new Map<ID, boolean>();

    collections?.forEach((collection) => {
      const cageSubjects = availableSubjects?.filter((subject) => subject.cage_id === collection.id) ?? [];
      subjectsMap.set(collection.id, cageSubjects);
      const hasUnresolvedAlert = cageSubjects.some((subject) =>
        Object.values(animalAlerts?.[subject.id] ?? {}).some((alert) => _isNil(alert.resolved_at))
      );
      alertsMap.set(collection.id, hasUnresolvedAlert);
    });

    return [subjectsMap, alertsMap];
  }, [availableSubjects, collections, animalAlerts]);

  const nameToShow = (subject: Animal, idToDisplay: AnimalIdentifier) =>
    _get(subject, animalIdsRecord[idToDisplay].animalIdKey, subject.name) ?? subject.name;

  const searchPlaceholderText = animalIdsRecord[idToSearch]?.title ?? 'Name';

  const CageRow = ({ index, style }: { index: number; style: CSSProperties }) => {
    const collection = collections[index];
    const subjectsInCollection = subjectsByCage.get(collection.id) ?? [];
    const cageHasAlert = cageHasAlerts.get(collection.id) ?? false;
    return (
      <ListItem
        title={collection.name}
        available={!disabled && subjectsInCollection.length > 0 && cageIds.includes(collection.id)}
        selected={collection.id === selectedCage}
        onClick={() => {
          changeCage({ type: 'SELECT_CAGE', cageId: collection.id });
        }}
        asideComponent={() => <span className="dark-gray">{subjectsInCollection.length}</span>}
        style={style}
        hasAlert={cageHasAlert}
      />
    );
  };

  const AnimalRow = ({ index, style }: { index: number; style: CSSProperties }) => {
    const subject = originalSelectedSubjects?.[index] ?? null;
    const available =
      (originalSelectedSubjects?.length ?? 0) > 0 &&
      !!selectedSubjects.find(({ catalog }) => catalog === subject?.catalog);
    const hasAlert = Object.values(animalAlerts?.[subject?.id ?? ''] ?? {}).some((alert) => _isNil(alert.resolved_at));
    const idValue = _get(subject, animalIdsRecord[idToSearch]?.animalIdKey ?? '');
    const hideEditId = idToSearch === 'name' && idToDisplay === 'name';
    const showEditSubjectId = idToSearch !== 'name';
    const aside = (id: ID) => (
      <>
        {!hideEditId && (
          <Tooltip render={showEditSubjectId ? `Edit ${searchPlaceholderText} ID` : null}>
            <span
              className="dark-gray small trunc"
              style={{ marginRight: _isNotEmpty(checklist[id]) ? '20px' : '' }}
              onClick={(event) => {
                if (showEditSubjectId) {
                  event.stopPropagation();
                  openModal('EDIT_SUBJECT_ID', {
                    closeModal,
                    id: {
                      name: searchPlaceholderText,
                      altId: idToSearch as AltId,
                      value: idValue,
                    },
                    subject,
                    onComplete: (value) => {
                      updateSingleSubject({
                        ...subject,
                        alt_ids: { ...subject?.alt_ids, [idToSearch]: value },
                      } as Animal);
                    },
                  } as EditSubjectIdProps);
                }
              }}
              data-testid="edit-subject-altid"
              title={idValue}
            >
              {ellipsisFromStart(idValue, ELLIPSIS_LENGTH)}
            </span>
          </Tooltip>
        )}
        {_isNotEmpty(checklist[id]) && <Checked />}
      </>
    );

    return (
      <>
        <ListItem
          title={subject ? nameToShow(subject, idToDisplay) : ''}
          available={available}
          selected={subject?.id === selectedAnimal}
          onClick={() => changeAnimal({ type: 'SELECT_ANIMAL', animalId: subject?.id ?? '' })}
          style={style}
          hasAlert={hasAlert}
          asideComponent={() => aside(subject?.id ?? '')}
        />
      </>
    );
  };

  return (
    <div className="flex flex-wrap justify-between w-40 h-100">
      <div ref={stageRef} className="ui__workflow__panel br b--moon-gray w-40 relative flex flex-wrap">
        <div className="w-100 self-start" data-testid="workflow-cage-list-container">
          <div className="pv3 ph4 bb b--moon-gray">
            <span className="basier-med lh-title f6 near-black">Cages</span>
          </div>
          {_notNil(height) && (
            <List<object>
              style={{ height: height - 102 }}
              rowCount={collections.length}
              rowHeight={33}
              rowComponent={CageRow}
              rowProps={{}}
              className="workflow-cage-list"
            />
          )}
        </div>
        <div className={`self-end w-100 bt b--moon-gray ${(deceased?.length ?? 0) < 1 && 'ui__disabled'} pv2 ph3`}>
          <a
            className={`flex flex-wrap justify-between items-center db lh-title f6 ${
              selectedCage === -1 ? activeMenuItemClasses : inActiveMenuItemClasses
            }`}
            onClick={() => {
              changeCage({ type: 'MARK_DECEASED' });
            }}
          >
            Deceased
            <Icon
              className="dark-gray"
              icon="arrow_right"
              viewBox="0 0 5 7"
              width="7"
              height="8"
              style={{ pointerEvents: 'none' }}
            />
          </a>
        </div>
      </div>
      <div className="ui__workflow__panel w-60 h-100 br b--moon-gray" data-testid="workflow-animal-list-container">
        <div className="pv3 ph4 bb b--moon-gray">
          <span className="basier-med lh-title f6 near-black">Animals</span>
        </div>
        {height && selectedCage && (
          <List<object>
            className="workflow-animal-list"
            style={{ height: height - 102 }}
            rowCount={originalSelectedSubjects?.length ?? 0}
            rowHeight={33}
            rowComponent={AnimalRow}
            rowProps={{}}
          />
        )}
      </div>
    </div>
  );
};

interface ListItemProps {
  title?: string;
  available?: unknown;
  selected?: unknown;
  onClick: MouseEventHandler<HTMLAnchorElement>;
  asideComponent?: () => React.ReactNode;
  style?: CSSProperties;
  hasAlert?: boolean;
}

const ListItem: React.FC<ListItemProps> = ({
  title,
  available,
  selected,
  onClick,
  asideComponent,
  style,
  hasAlert,
}) => {
  const inActiveMenuItemClasses = 'hover-blue hover-bg-moon-gray near-black';
  const activeMenuItemClasses = 'bg-near-white blue';

  return (
    <div className="mt1" style={style} data-test-component="ListItem" data-test-element="container">
      <a
        onClick={onClick}
        data-test-element="link"
        className={`flex justify-between relative lh-title pv2 pr3 pl4 mv2 mh3 db f6 pointer br2 ${
          !available && 'ui__disabled'
        } ${selected ? activeMenuItemClasses : inActiveMenuItemClasses}`}
      >
        <div className="trunc">
          {hasAlert && (
            <Indicator smallIndicator indicatorClassName="absolute" indicatorStyle={{ left: 16, top: 13 }} />
          )}
          <span>{title}</span>
        </div>
        {asideComponent && asideComponent()}
      </a>
    </div>
  );
};

const Checked: React.FC = () => (
  <span className="ui__wf__tick absolute ba br-100 ui__subject-selector__indicator ui__subject-selector__indicator__complete b--light-green bg-light-green white">
    <Icon icon="tick" width="12" height="12" viewBox="0 0 20 20" className="white percentCenter" />
  </span>
);

export default AnimalSelectPanel;
